import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getMemberDetails } from 'api/userApi';
import routes from 'routes';
import { memberListRouteForRole } from 'utils/memberLists';

import MetaTags from 'components/common/MetaTags';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import AdminPageHeader from 'components/admin/AdminPageHeader';
import MemberDetails from 'components/admin/MemberDetails';

const ShowMember = () => {
  const { id, email } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState(null);
  const [secondaryContact, setSecondaryContact] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [otherClubAthletes, setOtherClubAthletes] = useState([]);
  const [viewingAs, setViewingAs] = useState('Primary');
  const [error, setError] = useState(null);
  // Remember the loaded member's club role so a later bounce (e.g. a 404 after the member is removed)
  // returns to the matching list (visitors / guests / members) rather than always the Members list.
  const lastMemberRoleRef = useRef(null);

  const fetchMemberDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getMemberDetails(id, email);
      //console.log('Member details response:', response.data);

      setMember(response.data.member);
      setSecondaryContact(response.data.secondaryContact); // Will be null if none exists
      setAthletes(response.data.athletes || []);
      setOtherClubAthletes(response.data.otherClubAthletes || []);
      setViewingAs(response.data.viewingAs || 'Primary');
      lastMemberRoleRef.current = response.data.member?.role || null;
    } catch (err) {
      console.log('[ShowMember] Fetch member details error: ', err);

      if (err.response?.status === 403) {
        // User doesn't have permission to view this member
        toast.error('You do not have permission to view this member.');
        navigate(routes.admin.dashboard);
        return;
      } else if (err.response?.status === 404) {
        // Member not found — return to the list matching the member's class (visitors / guests /
        // members), using the role we last loaded, instead of always the Members list.
        toast.error('Member not found.');
        navigate(memberListRouteForRole(lastMemberRoleRef.current));
        return;
      } else {
        // Other errors - show error message but stay on page
        setError('Failed to load member information. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [id, email, navigate]);

  useEffect(() => {
    fetchMemberDetails();
  }, [fetchMemberDetails]);

  // Show error state if there's an error
  if (error) {
    return (
      <>
        <MetaTags title="SplitFast | Member Information" />
        <AdminTablePageLayout loading={false}>
          <AdminPageHeader showNumber={false} title="Member Information" />
          <div
            style={{
              padding: '2rem',
              textAlign: 'center',
              color: '#ef4444',
            }}
          >
            <h3>Error</h3>
            <p>{error}</p>
            <button
              onClick={fetchMemberDetails}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#cf2c47',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Try Again
            </button>
          </div>
        </AdminTablePageLayout>
      </>
    );
  }

  return (
    <>
      <MetaTags title="SplitFast | Member Information" />
      <AdminTablePageLayout loading={loading}>
        <AdminPageHeader showNumber={false} title="Member Information" />
        {member && (
          <MemberDetails
            member={member}
            clubId={id}
            secondaryContact={secondaryContact}
            athletes={athletes}
            otherClubAthletes={otherClubAthletes}
            viewingAs={viewingAs}
            onRefresh={fetchMemberDetails}
          />
        )}
      </AdminTablePageLayout>
    </>
  );
};

export default ShowMember;
